import React, { Dispatch, ReactNode, useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { startConversation } from '@repo/redux/actions/nexus';
import styles from './ContactCard.module.scss';
import Contact from '@repo/utils/models/Contact';
import { ID } from '@repo/utils/types';
import { ChevronRightIcon } from '@repo/icons';
import { ContactAvatar } from '@repo/components/avatars';

const contactName = (contact: Contact) => {
  let name = contact.displayName;

  if (contact.firstName) {
    name = contact.firstName;

    if (contact.lastName) {
      name = contact.firstName + ' ' + contact.lastName.trim()[0] + '.';
    }
  }

  return name;
};

export const Suggestion = ({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) => (
  <div className={cn(styles.suggestion, className)} onClick={onClick}>
    {children}
  </div>
);

type CardDetailType = 'birthday' | 'lastInteraction';
export const ContactCard = ({
  contact,
  className,
  onClick,
  onMouseEnter,
  detailType,
}: {
  contact: Contact;
  onClick?: () => void;
  detailType: CardDetailType;
  className?: string;
  onMouseEnter?: () => void;
}) => {
  let detail = '';

  if (detailType === 'birthday') {
    detail = `${contact.birthMonthAbbreviated} ${contact.birthday?.day}`;
  } else if (detailType === 'lastInteraction' && contact.lastInteractionDate) {
    detail = contact.lastInteractionDate.relativeTime;
  }

  return (
    <div
      className={cn(styles.card, className, !onClick && styles.noHover, !detail && styles.noDetail)}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      key={contact.id}
    >
      <div className={styles.cardContent}>
        <div className={styles.avatarContainer}>
          {contact.avatarURL ? (
            <div
              className={styles.avatarBlur}
              style={{
                backgroundImage: `url(${contact.avatarURL})`,
              }}
            />
          ) : (
            <ContactAvatar contact={contact} className={cn(styles.avatar, styles.avatarBlur)} />
          )}
          <ContactAvatar size="md" contact={contact} className={styles.avatar} />
        </div>
        <div className={styles.name}>{contactName(contact)}</div>
        {detail && <div className={cn('information', styles.detail)}>{detail}</div>}
      </div>
    </div>
  );
};

export const ContactCardsModule = ({
  contacts,
  contactToPrompt,
  detailType,
  renderPrompt,
  className,
  setTargetContactID,
  shouldSetPrompt,
  fallback,
  onFallbackClick,
}: {
  contacts: Contact[];
  contactToPrompt: (contact: Contact) => string;
  detailType: CardDetailType;
  renderPrompt: (prompt: string) => ReactNode;
  className?: string;
  setTargetContactID: Dispatch<ID>;
  shouldSetPrompt?: boolean;
  fallback?: string;
  onFallbackClick?: () => void;
}) => {
  const dispatch = useDispatch();
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const prompt = contactToPrompt(contacts[selectedIdx]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [contacts]);

  return (
    <div className={cn(styles.personModule, className)}>
      {contacts.length > 0 && (
        <>
          <div className={styles.contactCards}>
            {contacts.map((contact, idx) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                className={cn(idx == selectedIdx && styles.active)}
                onMouseEnter={() => setSelectedIdx(idx)}
                onClick={() => {
                  setTargetContactID(contact.id);
                  if (shouldSetPrompt) {
                    dispatch(startConversation(prompt, contact.id));
                  }
                }}
                detailType={detailType}
              />
            ))}
          </div>

          <Suggestion
            className={styles.promptSuggestion}
            onClick={() => {
              if (!shouldSetPrompt) {
                setTargetContactID(contacts[selectedIdx].id);
                return;
              }
              dispatch(startConversation(prompt, contacts[selectedIdx].id));
            }}
          >
            {renderPrompt(prompt)}
          </Suggestion>
        </>
      )}

      {contacts.length === 0 && fallback && onFallbackClick && (
        <Suggestion className={styles.fallbackSuggestion} onClick={onFallbackClick}>
          {fallback} <ChevronRightIcon width="15" height="15" />
        </Suggestion>
      )}
    </div>
  );
};
